import { ChangeEvent, FC, useEffect, useState } from 'react';

import { Box, OutlinedInput, Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material-next';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';

import {
    OUTPUT_LANGUAGES,
    TARGET_COUNTRIES,
    USER_SETTINGS,
} from 'utils/config/constants/controllerNames';
import { BRAND, BrandType, CLIENT } from 'components/DefaultSettings/types';
import PrimaryButton from 'components/Common/PrimaryButton';
import { onUpdateDoc } from 'utils/config/firebase';
import BasicModal from 'components/Common/BasicModal';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getTranslatedDataLanguages, getTranslatedTargetCountries } from 'helpers/filters';
import { setUserSettings } from 'store/auth/authSlice';
import { setOutputLanguages } from 'store/global/globalSlice';

import FormControlLabel from '@mui/material/FormControlLabel';
import { Radio, RadioGroup } from '@mui/material';

import { getBrands } from 'api/queries/languages';
import { useNavigate } from 'react-router-dom';

interface CountryAndLanguagesModalType {
    open: boolean;
    onClose: () => void;
    onCloseSidebar: () => void;
}

const CountryAndLanguagesModal: FC<CountryAndLanguagesModalType> = ({
    open,
    onClose,
    onCloseSidebar,
}) => {
    const { outputLanguages, targetCountries } = useAppSelector((state) => state.global);
    const { userSettings } = useAppSelector((state) => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [selectedBrand, setSelectedBrand] = useState<string>(userSettings?.brand ?? '');
    const [userType, setUserType] = useState<string>(userSettings?.user_type ?? BRAND);
    const [brands, setBrands] = useState<BrandType[] | []>([]);
    const [countryBrands, setCountryBrands] = useState<BrandType[] | []>([]);
    const [selectedOutputLanguage, setSelectedOutputLanguage] = useState<string>(
        userSettings?.output_language_title ?? '',
    );
    const [selectedTargetCountry, setSelectedTargetCountry] = useState<string>(
        userSettings?.target_country_code ?? '',
    );

    useEffect(() => {
        getBrands(setBrands);
    }, []);

    useEffect(() => {
        dispatch(
            setOutputLanguages(
                outputLanguages.map((item) =>
                    item.country_code === selectedTargetCountry
                        ? { ...item, shown: true }
                        : { ...item, shown: false },
                ),
            ),
        );
        if (brands.length > 0) {
            setCountryBrands(
                brands.filter((item) => item.countries?.includes(selectedTargetCountry)),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetCountries, selectedTargetCountry, brands]);

    const handleTargetSelect = (value: string) => {
        const newBrands = brands.filter((item) => item.countries?.includes(value));

        setCountryBrands(newBrands);
        if (!newBrands.find((item) => item.key === selectedBrand)) {
            setSelectedBrand('');
        }

        setSelectedOutputLanguage('');
        setSelectedTargetCountry(value);
    };

    const handleSelect = (event: SelectChangeEvent, type: string) => {
        const value = event.target.value;
        switch (type) {
            case OUTPUT_LANGUAGES:
                setSelectedOutputLanguage(value);
                break;
            case TARGET_COUNTRIES:
                handleTargetSelect(value);
                break;
            default:
                return;
        }
    };

    const onSubmit = () => {
        const selectedLanguage = outputLanguages.find(
            (item) => item.name === selectedOutputLanguage,
        );

        const targetCountryTitle = targetCountries?.find(
            (item) => item.country_code === selectedTargetCountry,
        )?.title;
        const data = {
            output_language_title: selectedOutputLanguage,
            output_language_code: selectedLanguage?.language_code,
            target_country: targetCountryTitle,
            target_country_code: selectedTargetCountry,
            brand: selectedBrand,
            user_type: userType,
        };
        if (userSettings) {
            onUpdateDoc(USER_SETTINGS, userSettings.document_id, data);
            dispatch(setUserSettings({ ...userSettings, ...data }));
        }
        onCloseSidebar();
        onClose();
        if (targetCountryTitle !== userSettings?.target_country) {
            navigate('/');
        }
    };

    const handleCheckBox = (e: ChangeEvent<HTMLInputElement>) => {
        setUserType(e.target.value);
        setSelectedBrand('');
    };

    const handleSelectBrand = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setSelectedBrand(value);
    };

    const isDisabled = (userType === BRAND && !selectedBrand) || selectedOutputLanguage === '';

    return (
        <BasicModal open={open} onClose={onClose}>
            <Box className="language-modal">
                <Box>
                    <FormControl className="language-modal-select">
                        <label>{t('web_app.settings.target_country_title')}</label>
                        <Select
                            labelId="country-label"
                            id="country-select"
                            value={selectedTargetCountry}
                            onChange={(e) => handleSelect(e, TARGET_COUNTRIES)}
                            label={t('web_app.settings.target_country_title')}
                            input={<OutlinedInput />}
                            inputProps={{ 'aria-label': 'Without label' }}
                        >
                            {getTranslatedTargetCountries(targetCountries).map((item) => (
                                <MenuItem key={item.document_id} value={item.country_code}>
                                    {item.title}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl className="language-modal-select">
                        <label>{t('web_app.settings.target_language_title')}</label>
                        <Select
                            labelId="job-language-label"
                            id="job-language-select"
                            value={selectedOutputLanguage}
                            onChange={(e) => handleSelect(e, OUTPUT_LANGUAGES)}
                            disabled={!selectedTargetCountry}
                            label={t('web_app.settings.target_language_title')}
                            input={<OutlinedInput className="language-modal-select-input" />}
                            inputProps={{ 'aria-label': 'Without label' }}
                        >
                            {getTranslatedDataLanguages(outputLanguages)
                                .filter((item) => item.shown)
                                .sort((a, b) => a.language_name.localeCompare(b.language_name))
                                .map((item) => (
                                    <MenuItem
                                        key={`job-advert-target-lang-${item.id}`}
                                        value={item.name}
                                    >
                                        {item.language_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box className="brands-modal">
                    <Box className="brands-modal-container">
                        <FormControl>
                            <RadioGroup
                                value={userType}
                                defaultValue={BRAND}
                                onChange={handleCheckBox}
                            >
                                <FormControlLabel
                                    value={CLIENT}
                                    control={<Radio />}
                                    label={
                                        <div>
                                            {t('web_app.default_settings.represent_client')}
                                            <div className="label-description">
                                                {t(
                                                    'web_app.default_settings.represent_client_description',
                                                )}
                                            </div>
                                        </div>
                                    }
                                    className="mb-16"
                                    disabled={!selectedTargetCountry}
                                />
                                <FormControlLabel
                                    value={BRAND}
                                    control={<Radio />}
                                    className="mb-16"
                                    disabled={!selectedTargetCountry}
                                    label={
                                        <div>
                                            {t('web_app.default_settings.represent_brand')}
                                            <div className="label-description">
                                                {t(
                                                    'web_app.default_settings.represent_brand_description',
                                                )}
                                            </div>
                                        </div>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                        <FormControl className="language-modal-select mt-10">
                            <Select
                                labelId="brand-label"
                                id="brand-select"
                                value={selectedBrand}
                                onChange={handleSelectBrand}
                                label={t('web_app.default_settings.represent_brand_title')}
                                disabled={userType !== BRAND}
                                input={<OutlinedInput />}
                                inputProps={{ 'aria-label': 'Without label' }}
                            >
                                {countryBrands.map((item) => (
                                    <MenuItem key={item.document_id} value={item.key}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box className="brands-modal-btn">
                        <PrimaryButton
                            onClick={onSubmit}
                            text={t('web_app.save_btn')}
                            disabled={isDisabled}
                        ></PrimaryButton>
                    </Box>
                </Box>
            </Box>
        </BasicModal>
    );
};

export default CountryAndLanguagesModal;
